import {ReactElement, useMemo} from 'react';
import {t} from 'i18next';
import {ReactComponent as DraftIcon} from '../../../../assests/svg/str_draft.svg';
import {ReactComponent as CompletedIcon} from '../../../../assests/svg/complete.svg';
import {ReactComponent as ProcessingIcon} from '../../../../assests/svg/status-processing.svg';
import {ReactComponent as FailedIcon} from '../../../../assests/svg/info-outlined-red.svg';

export enum STRStatusType {
  Draft = 'DRAFT',
  Ready = 'READY',
  Completed = 'COMPLETED',
  InProgress = 'IN_PROGRESS',
  Failed = 'FAILED',
  Pending = 'PENDING',
  CompletedWithError = 'COMPLETED_WITH_ERROR',
  Stopped = 'STOPPED',
}

export const getSTRStatusString = (status: string | null) => {
  const stringPath = 'eca.searchTermReport.viewSTR.statusFields.';
  let key = '';

  switch (getParseSTRStatus(status)) {
    case STRStatusType.Draft:
      key = 'draft';
      break;
    case STRStatusType.Completed:
      key = 'completed';
      break;
    case STRStatusType.InProgress:
      key = 'inProgress';
      break;
    case STRStatusType.Failed:
      key = 'failed';
      break;
    case STRStatusType.Ready:
      key = 'ready';
      break;
    case STRStatusType.Pending:
      key = 'pending';
      break;
    case STRStatusType.CompletedWithError:
      key = 'completedWithError';
      break;
    case STRStatusType.Stopped:
      key = 'stopped';
      break;
    default:
      break;
  }

  return key ? t(stringPath + key) : '';
};

const statusAliases: Record<string, STRStatusType> = {
  COMPLETED: STRStatusType.Completed,
  Completed: STRStatusType.Completed,
  IN_PROGRESS: STRStatusType.InProgress,
  InProgress: STRStatusType.InProgress,
  INPROGRESS: STRStatusType.InProgress,
  FAILED: STRStatusType.Failed,
  Failed: STRStatusType.Failed,
  DRAFT: STRStatusType.Draft,
  Draft: STRStatusType.Draft,
  READY: STRStatusType.Ready,
  Ready: STRStatusType.Ready,
  Pending: STRStatusType.Pending,
  PENDING: STRStatusType.Pending,
  CompletedWithError: STRStatusType.CompletedWithError,
  COMPLETED_WITH_ERROR: STRStatusType.CompletedWithError,
  COMPLETEDWITHERROR: STRStatusType.CompletedWithError,
  STOPPED: STRStatusType.Stopped,
  Stopped: STRStatusType.Stopped,
};

export const getParseSTRStatus = (value: string | null): STRStatusType => {
  return statusAliases[value ?? ''];
};

/**
 * Check report is completed by checking different status because all this
 * status have same behviour for some functionality same a Complete Status
 * @param status
 * @returns true if status is one of Completed, WithError, Failed or Stopped.
 */
export const isConsiderCompletedSTR = (status: STRStatusType | null) => {
  return (
    status === STRStatusType.Completed ||
    status === STRStatusType.CompletedWithError ||
    status === STRStatusType.Failed ||
    status === STRStatusType.Stopped
  );
};

/** hook for return term report list array with
 * localize string for filter functionality in str report list page
 * */
export const useSTRReportStatusList = () => {
  return [
    {
      id: 1,
      label: t('status.draft'),
      value: 'Draft',
    },
    {
      id: 2,
      label: t('status.ready'),
      value: 'Ready',
    },
    {
      id: 3,
      label: t('status.inProgress'),
      value: 'INPROGRESS',
    },
    {
      id: 4,
      label: t('status.completed'),
      value: 'Completed',
    },
    {
      id: 5,
      label: t('status.failed'),
      value: 'Failed',
    },
    {
      id: 6,
      label: t('status.completedWithError'),
      value: 'COMPLETEDWITHERROR',
    },
    {
      id: 7,
      label: t('status.stopped'),
      value: 'STOPPED',
    },
  ];
};

/** hook for return term status list array with
 * localize string for filter functionality in report detail page
 * */
export const useSTRTermStatusList = () => {
  return [
    {
      id: 1,
      label: t('status.pending'),
      value: 'PENDING',
    },
    {
      id: 2,
      label: t('status.inProgress'),
      value: 'INPROGRESS',
    },
    {
      id: 3,
      label: t('status.completed'),
      value: 'COMPLETED',
    },
    {
      id: 4,
      label: t('status.failed'),
      value: 'FAILED',
    },
  ];
};

const useSTRStatus = (status: string | null) => {
  const statusData = useMemo(() => {
    const title = getSTRStatusString(getParseSTRStatus(status));
    let icon: ReactElement | undefined = undefined;
    let progressBarVisible = false;
    switch (status) {
      case STRStatusType.Draft:
        icon = <DraftIcon />;
        break;
      case STRStatusType.Completed:
        icon = <CompletedIcon aria-hidden="true" />;
        break;
      case STRStatusType.InProgress:
        icon = <ProcessingIcon aria-hidden="true" />;
        progressBarVisible = true;
        break;
      case STRStatusType.Failed:
      case STRStatusType.CompletedWithError:
        icon = <FailedIcon width={15} height={15} aria-hidden="true" />;
        break;
      default:
        break;
    }
    return {title: t(title), icon, progressBarVisible};
  }, [status]);

  return {
    StatusIcon: statusData.icon,
    statusName: statusData.title,
    statusProgressBarShow: statusData.progressBarVisible,
  };
};

export default useSTRStatus;
